import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CatalogService } from '@services/data/catalog.service';
import { ProcessDocumentService } from '../../services/process-document.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import {
  saveRequirementsSwalOptions,
  saveRequirementsSuccessText
} from '../../utils/doc-requirement-audit.utils';
import Swal from 'sweetalert2';

interface GroupedRequirement {
    id: number;
    name: string;
    items: any[];
}

@Component({
    selector: 'app-requirements-list',
    standalone: true,
    imports: [CommonModule, FormsModule, ListCardComponent, MobileFooterComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './requirements-list.component.html'
})
export class RequirementsListComponent {
    @Input() requirements: any[] = [];
    @Input() processSlug!: string;
    @Output() manageRequirements = new EventEmitter<void>();
    @Output() syncRequirements = new EventEmitter<void>();

    public catalogService = inject(CatalogService);
    public appSettings = inject(AppSettings);
    private processService = inject(ProcessDocumentService);

    isEditMode = false;
    tempRequirements: any[] = [];


    get groupedRequirements(): GroupedRequirement[] {
        const source = this.isEditMode ? this.tempRequirements : this.requirements;
        const groups = source.reduce((acc, req) => {
            const categoryKey = req.categorySlug ?? 'OTHER';
            if (!acc[categoryKey]) {
                acc[categoryKey] = { 
                    id: categoryKey, 
                    name: this.catalogService.getCategoryName(categoryKey) ?? 'Otros', 
                    items: [] 
                };
            }
            acc[categoryKey].items.push(req);
            return acc;
        }, {} as Record<string, any>);

        return this.orderByGroup(groups);
    }

    private compareRequirements(a: any, b: any): number {
        const byOwner = String(a.ownerSlug || '').localeCompare(String(b.ownerSlug || ''), 'es');
        if (byOwner) return byOwner;
        const an = a.type?.name || this.catalogService.getDocumentTypeName(a.typeSlug) || a.typeSlug || '';
        const bn = b.type?.name || this.catalogService.getDocumentTypeName(b.typeSlug) || b.typeSlug || '';
        return String(an).localeCompare(String(bn), 'es');
    }

    orderByGroup(groups: Record<string, any>) {
        return Object.values(groups)
            .map((g: any) => ({
                ...g,
                items: [...(g.items || [])].sort((a, b) => this.compareRequirements(a, b))
            }))
            .sort((a: any, b: any) => a.name.localeCompare(b.name, 'es')) as any[];
    }
    toggleEditMode(): void {
        this.isEditMode = true;
        this.tempRequirements = JSON.parse(JSON.stringify(this.requirements));
    }

    cancelEdit(): void {
        this.isEditMode = false;
        this.tempRequirements = [];
    }

    async saveAll(): Promise<void> {
        const choice = await Swal.fire(saveRequirementsSwalOptions());
        if (choice.isDismissed) return;
        const applyToOpen = choice.isDenied;
        // Persistir Estado (isActive) tal cual; tipos inactivos no entran al snapshot de solicitudes.
        Swal.fire({ title: 'Guardando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        this.processService.saveRequirementsBulk(this.processSlug, this.tempRequirements, { applyToOpen }).subscribe({
            next: (res) => this.onBulkSaveSuccess(applyToOpen, res?.affectedCount ?? 0),
            error: (err) => Swal.fire('Error', 'No se pudieron guardar los cambios: ' + (err.error?.message ?? err.message), 'error')
        });
    }

    private onBulkSaveSuccess(applyToOpen: boolean, affectedCount: number): void {
        this.isEditMode = false;
        // Reflejar isActive local en catálogo (el bulk ya lo persistió)
        for (const req of this.tempRequirements) {
            if (req?.typeSlug && req.type) {
                this.catalogService.upsertDocumentType({
                    ...(this.catalogService.getDocumentTypeBySlug(req.typeSlug) || {}),
                    ...req.type,
                    slug: req.typeSlug,
                    isActive: req.type.isActive !== false
                });
            }
        }
        this.catalogService.setRequirementsForProcess(this.processSlug, this.tempRequirements);
        this.requirements = this.catalogService.getRequirementsByProcess(this.processSlug);
        this.tempRequirements = [];
        Swal.fire({
            icon: 'success',
            title: 'Requisitos guardados',
            text: saveRequirementsSuccessText(applyToOpen, affectedCount),
            timer: 2800,
            showConfirmButton: false
        });
    }

    /** Solo en modo Editar: cambia isActive en memoria; se guarda con bulk + alcance. */
    toggleActive(req: any): void {
        if (!this.isEditMode) return;
        const type = req.type ?? this.catalogService.getDocumentTypeBySlug(req.typeSlug);
        if (!type) return;
        const nextActive = !(type.isActive !== false);
        // Inactive ⇒ fuera del snapshot; si era obligatorio, también se apaga.
        // Activo + no obligatorio = complementario: va al snapshot, no se precarga.
        req.type = { ...type, isActive: nextActive };
        if (!nextActive) req.isRequired = false;
    }

    /** Obligatorio ⇒ debe estar activo. Activo no implica obligatorio. */
    onRequiredChange(req: any): void {
        if (!this.isEditMode) return;
        if (req.isRequired === false) return;
        req.isRequired = true;
        const type = req.type ?? this.catalogService.getDocumentTypeBySlug(req.typeSlug);
        if (!type) return;
        req.type = { ...type, isActive: true };
    }

    private notifySuccess(title: string): void {
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title, showConfirmButton: false, timer: 2000 });
    }
}
